import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Package, FileText, LogOut, ShoppingCart, RefreshCw, Loader2, ShieldCheck, Headset, Building2, Upload, CheckCircle2, Clock, CreditCard, Heart, MapPin, Trash2, Plus, Star, Check } from "lucide-react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import Seo from "@/components/Seo";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";

const input = "w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30";
const SERVICES = ["Création de jardin", "Entretien", "Élagage", "Terrassement", "Bassin & irrigation", "Clôture / portail"];

export default function Compte() {
  const { customer, login, register, logout } = useCustomerAuth();
  if (customer === null) return <div className="pt-40 pb-40 text-center text-stone">Chargement…</div>;
  return (
    <div className="bg-cream min-h-screen">
      <Seo title="Mon compte" path="/compte" noindex />
      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-24">
        {customer ? <Dashboard customer={customer} logout={logout} /> : <AuthForms login={login} register={register} />}
      </div>
    </div>
  );
}

function AuthForms({ login, register }) {
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", account_type: "particulier", company: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "login") await login({ email: f.email, password: f.password });
      else await register(f);
      toast.success("Bienvenue !");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  const quickLogin = async (email, password) => {
    setBusy(true);
    try { await login({ email, password }); toast.success("Bienvenue !"); }
    catch { toast.error("Échec de connexion rapide"); } finally { setBusy(false); }
  };
  const { login: adminLogin } = useAuth();
  const nav = useNavigate();
  const backofficeLogin = async (email, password, label) => {
    setBusy(true);
    try { await adminLogin(email, password); toast.success(`Connecté : ${label}`); nav("/admin"); }
    catch { toast.error("Échec de connexion back-office"); setBusy(false); }
  };
  const TYPES = [{ v: "particulier", l: "Particulier" }, { v: "entreprise", l: "Entreprise" }, { v: "collectivite", l: "Collectivité" }];
  return (
    <div className="max-w-md mx-auto">
      <h1 className="font-display text-4xl text-forest text-center mb-2">Mon compte</h1>
      <p className="text-stone text-center mb-8">Retrouvez vos commandes, votre panier et vos demandes de devis.</p>
      <div className="rounded-2xl bg-white border border-border p-6">
        <div className="flex rounded-full bg-sage p-1 mb-6">
          <button onClick={() => setMode("login")} data-testid="tab-login" className={`flex-1 rounded-full py-2 text-sm font-semibold ${mode === "login" ? "bg-brand text-cream" : "text-stone"}`}>Connexion</button>
          <button onClick={() => setMode("register")} data-testid="tab-register" className={`flex-1 rounded-full py-2 text-sm font-semibold ${mode === "register" ? "bg-brand text-cream" : "text-stone"}`}>Inscription</button>
        </div>
        <div className="space-y-3">
          {mode === "register" && <>
            <div>
              <label className="text-xs text-stone">Type de compte</label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {TYPES.map((t) => <button key={t.v} type="button" onClick={() => setF({ ...f, account_type: t.v })} data-testid={`acc-${t.v}`} className={`rounded-lg border py-2 text-xs font-medium ${f.account_type === t.v ? "border-brand bg-brand/5 text-brand" : "border-border text-stone"}`}>{t.l}</button>)}
              </div>
            </div>
            <input className={input} placeholder="Nom & prénom" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="reg-name" />
            {f.account_type !== "particulier" && <>
              <input className={input} placeholder="Nom de la structure" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} data-testid="reg-company" />
              <input className={input} placeholder="SIRET" value={f.siret || ""} onChange={(e) => setF({ ...f, siret: e.target.value })} data-testid="reg-siret" />
            </>}
            <input className={input} placeholder="Téléphone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="reg-phone" />
          </>}
          <input className={input} type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="auth-email" />
          <input className={input} type="password" placeholder="Mot de passe" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="auth-password" />
          <button onClick={submit} disabled={busy} data-testid="auth-submit" className="w-full rounded-full bg-brand text-cream py-3 font-semibold hover:bg-forest flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <User size={18} />} {mode === "login" ? "Se connecter" : "Créer mon compte"}
          </button>
        </div>
        <div className="mt-5 pt-4 border-t border-border">
          <p className="text-[11px] uppercase tracking-wider text-stone/60 text-center mb-2">Connexion rapide (test)</p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => quickLogin("particulier@test.fr", "Test2026!")} disabled={busy} data-testid="quick-particulier"
              className="rounded-lg bg-brand/10 text-brand text-xs font-semibold py-2.5 hover:bg-brand hover:text-cream transition-colors disabled:opacity-60">🌿 Client particulier</button>
            <button type="button" onClick={() => quickLogin("pro@test.fr", "Test2026!")} disabled={busy} data-testid="quick-pro"
              className="rounded-lg bg-forest/10 text-forest text-xs font-semibold py-2.5 hover:bg-forest hover:text-cream transition-colors disabled:opacity-60">🏢 Client pro</button>
          </div>
          <p className="text-[11px] uppercase tracking-wider text-stone/60 text-center mt-4 mb-2">Accès back-office (test)</p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => backofficeLogin("admin@acanthejardins.fr", "Acanthe2026!", "Admin")} disabled={busy} data-testid="quick-backoffice-admin"
              className="rounded-lg bg-terracotta/15 text-terracotta text-xs font-semibold py-2.5 hover:bg-terracotta hover:text-cream transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"><ShieldCheck size={14} /> Admin du site</button>
            <button type="button" onClick={() => backofficeLogin("service@acanthejardins.fr", "Service2026!", "Service client")} disabled={busy} data-testid="quick-backoffice-service"
              className="rounded-lg bg-forest/10 text-forest text-xs font-semibold py-2.5 hover:bg-forest hover:text-cream transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"><Headset size={14} /> Service client</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const WELCOME = {
  particulier: "Ravis de vous accueillir ! Composez le jardin méditerranéen de vos rêves.",
  entreprise: "Bienvenue ! Bénéficiez d'un accompagnement dédié pour vos projets professionnels.",
  collectivite: "Bienvenue ! Nous accompagnons vos espaces verts publics avec des solutions durables.",
};
const TYPE_BADGE = { particulier: "Particulier", entreprise: "Entreprise", collectivite: "Collectivité" };

function Dashboard({ customer, logout }) {
  const [tab, setTab] = useState("orders");
  const type = customer.account_type || "particulier";
  const initial = (customer.company || customer.name || "?").trim()[0]?.toUpperCase() || "?";
  const isPro = type === "entreprise" || type === "collectivite";
  const menu = [
    { id: "orders", label: "Mes commandes", icon: Package },
    { id: "favorites", label: "Mes favoris", icon: Heart },
    { id: "addresses", label: "Mes adresses", icon: MapPin },
    { id: "cart", label: "Mon panier", icon: ShoppingCart },
    ...(isPro ? [{ id: "pro", label: "Compte professionnel", icon: Building2 }] : []),
  ];
  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-8" data-testid="account-dashboard">
      {/* Sidebar */}
      <aside className="lg:sticky lg:top-28 h-fit" data-testid="account-sidebar">
        <div className="rounded-2xl bg-white border border-border p-5">
          <div className="flex items-center gap-3 pb-4 mb-4 border-b border-border">
            <div className="h-12 w-12 rounded-full bg-brand text-cream flex items-center justify-center font-display text-xl shrink-0">{initial}</div>
            <div className="min-w-0">
              <p className="font-semibold text-forest truncate">{customer.company || customer.name}</p>
              <span className="inline-block mt-0.5 rounded-full bg-sage text-forest text-[11px] font-semibold px-2 py-0.5" data-testid="account-type-badge">{TYPE_BADGE[type]}</span>
            </div>
          </div>
          <nav className="space-y-1">
            {menu.map((m) => (
              <button key={m.id} onClick={() => setTab(m.id)} data-testid={`tab-${m.id}`}
                className={`w-full flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${tab === m.id ? "bg-brand text-cream" : "text-stone hover:bg-sage/60 hover:text-forest"}`}>
                <m.icon size={17} /> {m.label}
              </button>
            ))}
          </nav>
          <button onClick={logout} data-testid="logout-btn" className="w-full mt-4 pt-4 border-t border-border flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-red-500 hover:bg-red-50 transition-colors">
            <LogOut size={17} /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="min-w-0">
        <div className="mb-8">
          <h1 className="font-display text-4xl text-forest">Bonjour, {customer.company || customer.name?.split(" ")[0]}</h1>
          <p className="text-stone mt-1" data-testid="welcome-message">{WELCOME[type]}</p>
        </div>
        {tab === "pro" ? <ProAccount customer={customer} />
          : tab === "favorites" ? <FavoritesTab />
          : tab === "addresses" ? <AddressesTab customer={customer} />
          : tab === "cart" ? <SavedCartTab />
          : <Orders customer={customer} />}
      </div>
    </div>
  );
}

function Orders({ customer }) {
  const [orders, setOrders] = useState([]);
  const { add } = useCart();
  useEffect(() => { api.get("/shop/my-orders").then((r) => setOrders(r.data)).catch(() => {}); }, []);
  const reorder = (o) => { o.items.forEach((it) => add({ id: it.product_id, slug: it.slug || "", name: it.name, price: it.price, image: it.image }, it.quantity)); toast.success("Produits ajoutés au panier"); };
  if (orders.length === 0) return <div className="rounded-2xl bg-white border border-dashed border-border py-16 text-center text-stone">Aucune commande pour l'instant.</div>;
  return (
    <div className="space-y-4">
      {orders.map((o) => (
        <div key={o.id} className="rounded-2xl bg-white border border-border p-5" data-testid={`myorder-${o.order_id}`}>
          <div className="flex items-center justify-between mb-3">
            <div><p className="font-semibold text-forest">{o.total?.toFixed(2)} €</p><p className="text-xs text-stone">{new Date(o.created_at).toLocaleDateString("fr-FR")} · {o.status}</p></div>
            <button onClick={() => reorder(o)} data-testid={`reorder-${o.order_id}`} className="rounded-full bg-brand/10 text-brand px-4 py-2 text-sm font-semibold hover:bg-brand hover:text-cream flex items-center gap-1.5"><RefreshCw size={14} /> Recommander</button>
          </div>
          <div className="flex flex-wrap gap-3">
            {o.items?.map((it, k) => (
              <div key={k} className="flex items-center gap-2 rounded-lg bg-[#F7F7F8] px-3 py-2">
                {it.image && <img src={fileUrl(it.image)} alt="" className="h-8 w-8 rounded object-cover" />}
                <span className="text-sm text-forest">{it.name} × {it.quantity}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function FavoritesTab() {
  const [products, setProducts] = useState(null);
  const { add } = useCart();
  const nav = useNavigate();
  const load = () => api.get("/shop/favorites").then((r) => setProducts(r.data.products)).catch(() => setProducts([]));
  useEffect(() => { load(); }, []);
  const removeFav = async (pid) => { await api.delete(`/shop/favorites/${pid}`); load(); };
  if (products === null) return <div className="py-16 text-center text-stone"><Loader2 className="animate-spin mx-auto" /></div>;
  if (products.length === 0) return <div className="rounded-2xl bg-white border border-dashed border-border py-16 text-center text-stone" data-testid="favorites-empty">Aucun produit favori. Cliquez sur le ❤️ d'un produit pour l'ajouter.</div>;
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="favorites-list">
      {products.map((p) => (
        <div key={p.id} className="rounded-2xl bg-white border border-border p-4 flex gap-3" data-testid={`fav-${p.id}`}>
          <img src={fileUrl(p.image)} alt={p.name} className="h-20 w-20 rounded-lg object-cover shrink-0 cursor-pointer" onClick={() => nav(`/boutique/${p.slug}`)} />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-forest truncate cursor-pointer hover:text-brand" onClick={() => nav(`/boutique/${p.slug}`)}>{p.name}</p>
            <p className="text-sm text-brand font-semibold">{Number(p.effective_price || p.price).toFixed(2)} €</p>
            <div className="flex gap-2 mt-2">
              <button onClick={() => { add({ id: p.id, slug: p.slug, name: p.name, price: p.effective_price || p.price, image: p.image }); toast.success("Ajouté au panier"); }} data-testid={`fav-add-${p.id}`} className="rounded-full bg-brand text-cream text-xs font-semibold px-3 py-1.5 hover:bg-forest flex items-center gap-1"><ShoppingCart size={13} /> Ajouter</button>
              <button onClick={() => removeFav(p.id)} data-testid={`fav-remove-${p.id}`} className="rounded-full border border-border text-red-500 text-xs px-3 py-1.5 hover:bg-red-50 flex items-center gap-1"><Trash2 size={13} /> Retirer</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const emptyAddr = { label: "", name: "", address: "", postal_code: "", city: "", phone: "", is_default: false };

function AddressesTab({ customer }) {
  const [billing, setBilling] = useState({ address: "", postal_code: "", city: "" });
  const [addresses, setAddresses] = useState([]);
  const [form, setForm] = useState(emptyAddr);
  const [editing, setEditing] = useState(null);
  const [savingBill, setSavingBill] = useState(false);
  const load = () => api.get("/shop/account/addresses").then((r) => { setBilling(r.data.billing_address || { address: "", postal_code: "", city: "" }); setAddresses(r.data.addresses || []); });
  useEffect(() => { load(); }, []);

  const saveBilling = async () => { setSavingBill(true); try { await api.put("/shop/account/billing", billing); toast.success("Adresse de facturation enregistrée"); } catch { toast.error("Erreur"); } finally { setSavingBill(false); } };
  const saveAddr = async () => {
    if (!form.address || !form.postal_code || !form.city) { toast.error("Adresse, code postal et ville requis"); return; }
    try {
      if (editing) await api.put(`/shop/account/addresses/${editing}`, form);
      else await api.post("/shop/account/addresses", form);
      toast.success("Adresse enregistrée"); setForm(emptyAddr); setEditing(null); load();
    } catch { toast.error("Erreur"); }
  };
  const editAddr = (a) => { setForm(a); setEditing(a.id); };
  const delAddr = async (id) => { await api.delete(`/shop/account/addresses/${id}`); load(); };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-white border border-border p-5" data-testid="billing-section">
        <h3 className="font-semibold text-forest mb-3 flex items-center gap-2"><CreditCard size={17} className="text-brand" /> Adresse de facturation</h3>
        <div className="space-y-2.5">
          <input className={input} placeholder="Adresse" value={billing.address || ""} onChange={(e) => setBilling({ ...billing, address: e.target.value })} data-testid="billing-address-input" />
          <div className="grid grid-cols-3 gap-2.5">
            <input className={input} placeholder="Code postal" value={billing.postal_code || ""} onChange={(e) => setBilling({ ...billing, postal_code: e.target.value })} data-testid="billing-postal-input" />
            <input className={input + " col-span-2"} placeholder="Ville" value={billing.city || ""} onChange={(e) => setBilling({ ...billing, city: e.target.value })} data-testid="billing-city-input" />
          </div>
          <button onClick={saveBilling} disabled={savingBill} data-testid="billing-save" className="rounded-full bg-brand text-cream px-5 py-2 text-sm font-semibold hover:bg-forest disabled:opacity-60">Enregistrer</button>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-border p-5" data-testid="addresses-section">
        <h3 className="font-semibold text-forest mb-3 flex items-center gap-2"><MapPin size={17} className="text-brand" /> Adresses de livraison</h3>
        {addresses.length > 0 && (
          <div className="space-y-2.5 mb-5">
            {addresses.map((a) => (
              <div key={a.id} className="flex items-start justify-between gap-3 rounded-xl border border-border p-3" data-testid={`addr-${a.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-forest flex items-center gap-2">{a.label || "Adresse"} {a.is_default && <span className="text-[10px] bg-brand/10 text-brand px-2 py-0.5 rounded-full flex items-center gap-0.5"><Check size={10} /> Par défaut</span>}</p>
                  <p className="text-sm text-stone">{a.address}, {a.postal_code} {a.city}{a.phone ? ` · ${a.phone}` : ""}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => editAddr(a)} data-testid={`addr-edit-${a.id}`} className="text-brand hover:bg-brand/5 rounded-lg p-1.5 text-xs">Modifier</button>
                  <button onClick={() => delAddr(a.id)} data-testid={`addr-delete-${a.id}`} className="text-red-500 hover:bg-red-50 rounded-lg p-1.5"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="rounded-xl bg-[#F7F7F8] p-4 space-y-2.5" data-testid="address-form">
          <p className="text-sm font-semibold text-forest">{editing ? "Modifier l'adresse" : "Ajouter une adresse"}</p>
          <div className="grid sm:grid-cols-2 gap-2.5">
            <input className={input} placeholder="Libellé (Maison, Bureau…)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="addr-label" />
            <input className={input} placeholder="Nom du destinataire" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="addr-name" />
          </div>
          <input className={input} placeholder="Adresse (n° et rue)" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="addr-address" />
          <div className="grid grid-cols-3 gap-2.5">
            <input className={input} placeholder="Code postal" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="addr-postal" />
            <input className={input + " col-span-2"} placeholder="Ville" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="addr-city" />
          </div>
          <input className={input} placeholder="Téléphone (optionnel)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="addr-phone" />
          <label className="flex items-center gap-2 text-sm text-forest cursor-pointer"><input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="h-4 w-4 accent-[#2E9E2E]" data-testid="addr-default" /> Définir comme adresse par défaut</label>
          <div className="flex gap-2">
            <button onClick={saveAddr} data-testid="addr-save" className="rounded-full bg-brand text-cream px-5 py-2 text-sm font-semibold hover:bg-forest flex items-center gap-1"><Plus size={15} /> {editing ? "Enregistrer" : "Ajouter"}</button>
            {editing && <button onClick={() => { setForm(emptyAddr); setEditing(null); }} className="rounded-full border border-border px-4 py-2 text-sm text-stone">Annuler</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function SavedCartTab() {
  const [items, setItems] = useState(null);
  const { add } = useCart();
  const nav = useNavigate();
  useEffect(() => { api.get("/shop/cart").then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, []);
  const restore = () => { items.forEach((it) => add({ id: it.product_id || it.id, slug: it.slug || "", name: it.name, price: it.price, image: it.image }, it.quantity || 1)); toast.success("Panier restauré"); nav("/boutique/panier"); };
  if (items === null) return <div className="py-16 text-center text-stone"><Loader2 className="animate-spin mx-auto" /></div>;
  if (items.length === 0) return <div className="rounded-2xl bg-white border border-dashed border-border py-16 text-center text-stone" data-testid="savedcart-empty">Aucun panier sauvegardé. Votre panier est synchronisé automatiquement quand vous êtes connecté.</div>;
  const total = items.reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0);
  return (
    <div className="rounded-2xl bg-white border border-border p-5" data-testid="savedcart">
      <h3 className="font-semibold text-forest mb-3 flex items-center gap-2"><ShoppingCart size={17} className="text-brand" /> Mon panier sauvegardé</h3>
      <div className="space-y-2.5 mb-4">
        {items.map((it, k) => (
          <div key={k} className="flex items-center gap-3 rounded-lg bg-[#F7F7F8] px-3 py-2">
            {it.image && <img src={fileUrl(it.image)} alt="" className="h-9 w-9 rounded object-cover" />}
            <span className="text-sm text-forest flex-1">{it.name} × {it.quantity || 1}</span>
            <span className="text-sm font-semibold text-forest">{((it.price || 0) * (it.quantity || 1)).toFixed(2)} €</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <span className="font-semibold text-forest">Total : {total.toFixed(2)} €</span>
        <button onClick={restore} data-testid="restore-cart" className="rounded-full bg-brand text-cream px-5 py-2.5 text-sm font-semibold hover:bg-forest flex items-center gap-1.5"><RefreshCw size={15} /> Reprendre mon panier</button>
      </div>
    </div>
  );
}


function QuoteTab({ customer }) {
  const [f, setF] = useState({ name: customer.name, email: customer.email, phone: customer.phone || "", garden_area: "", garden_type: "", budget: "", services: [], message: "" });
  const [quotes, setQuotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/shop/my-quotes").then((r) => setQuotes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = (s) => setF((p) => ({ ...p, services: p.services.includes(s) ? p.services.filter((x) => x !== s) : [...p.services, s] }));
  const submit = async () => {
    if (!f.garden_area && !f.message) { toast.error("Décrivez votre projet"); return; }
    setBusy(true);
    try { await api.post("/shop/quotes", f); toast.success("Demande envoyée ! Nous vous recontactons vite."); setF({ ...f, garden_area: "", garden_type: "", budget: "", services: [], message: "" }); load(); }
    catch { toast.error("Erreur"); } finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6">
      <div className="rounded-2xl bg-white border border-border p-6" data-testid="quote-form">
        <h3 className="font-display text-2xl text-forest mb-1">Demande de devis — aménagement extérieur</h3>
        <p className="text-sm text-stone mb-5">Décrivez votre jardin, nous établissons une proposition sur mesure.</p>
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-xs text-stone">Superficie du jardin</label><input className={input} placeholder="ex : 500 m²" value={f.garden_area} onChange={(e) => setF({ ...f, garden_area: e.target.value })} data-testid="q-area" /></div>
            <div><label className="text-xs text-stone">Type de terrain</label><input className={input} placeholder="plat, en pente, argileux…" value={f.garden_type} onChange={(e) => setF({ ...f, garden_type: e.target.value })} data-testid="q-type" /></div>
          </div>
          <div><label className="text-xs text-stone">Budget estimé</label><input className={input} placeholder="ex : 5 000 €" value={f.budget} onChange={(e) => setF({ ...f, budget: e.target.value })} data-testid="q-budget" /></div>
          <div>
            <label className="text-xs text-stone">Prestations souhaitées</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {SERVICES.map((s) => <button key={s} type="button" onClick={() => toggle(s)} className={`rounded-full px-3 py-1.5 text-xs border ${f.services.includes(s) ? "bg-brand text-cream border-brand" : "border-border text-stone"}`}>{s}</button>)}
            </div>
          </div>
          <textarea rows={4} className={input} placeholder="Décrivez votre projet (arbres souhaités, style, contraintes…)" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="q-message" />
          <button onClick={submit} disabled={busy} data-testid="q-submit" className="w-full rounded-full bg-brand text-cream py-3 font-semibold hover:bg-forest flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <Loader2 className="animate-spin" size={18} /> : <FileText size={18} />} Envoyer ma demande</button>
        </div>
      </div>
      <div>
        <h3 className="font-semibold text-forest mb-3">Mes demandes</h3>
        {quotes.length === 0 ? <p className="text-sm text-stone">Aucune demande.</p> : (
          <div className="space-y-3">{quotes.map((q) => (
            <div key={q.id} className="rounded-xl bg-white border border-border p-4 text-sm">
              <div className="flex justify-between"><span className="font-medium text-forest">{q.garden_area || "Projet"}</span><span className="text-xs rounded bg-amber-100 text-amber-700 px-2 py-0.5">{q.status}</span></div>
              <p className="text-xs text-stone mt-1">{new Date(q.created_at).toLocaleDateString("fr-FR")} · {q.services?.join(", ")}</p>
            </div>
          ))}</div>
        )}
      </div>
    </div>
  );
}


const DOC_LIST = [
  { k: "kbis", l: "Extrait Kbis (moins de 3 mois)" },
  { k: "cni", l: "CNI du gérant" },
  { k: "rib", l: "RIB" },
];
const ACC_STATUS = {
  incomplet: { l: "À compléter", c: "bg-amber-100 text-amber-700" },
  en_attente: { l: "En attente de validation", c: "bg-blue-100 text-blue-700" },
  approuve: { l: "Compte approuvé", c: "bg-green-100 text-green-700" },
  refuse: { l: "Refusé", c: "bg-red-100 text-red-700" },
};

function ProAccount({ customer }) {
  const [me, setMe] = useState(customer);
  const [busy, setBusy] = useState("");
  const refresh = () => api.get("/shop/auth/me").then((r) => setMe(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);
  const docs = me.documents || {};
  const status = me.account_status || "incomplet";
  const allUploaded = DOC_LIST.every((d) => docs[d.k]);

  const upload = async (k, file) => {
    if (!file) return;
    setBusy(k);
    const fd = new FormData(); fd.append("doc_type", k); fd.append("file", file);
    try { await api.post("/shop/account/documents", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Document enregistré"); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'envoi"); } finally { setBusy(""); }
  };
  const submitRequest = async () => {
    setBusy("submit");
    try { await api.post("/shop/account/submit-request"); toast.success("Demande d'ouverture de compte envoyée !"); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(""); }
  };

  const st = ACC_STATUS[status] || ACC_STATUS.incomplet;
  return (
    <div className="space-y-6" data-testid="pro-account">
      <div className="rounded-2xl bg-white border border-border p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-2xl text-forest">Compte professionnel</h3>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${st.c}`} data-testid="pro-status">{st.l}</span>
        </div>
        <p className="text-sm text-stone mb-5">Ouvrez un compte pro pour bénéficier du <strong>paiement à 30 jours fin de mois</strong> (soumis à validation). Sinon, vous pouvez commander en ligne par carte bancaire à tout moment.</p>

        {status === "approuve" && (
          <div className="rounded-xl bg-green-50 border border-green-200 p-4 mb-5" data-testid="pro-approved">
            <p className="text-sm text-green-800 flex items-center gap-2"><CheckCircle2 size={17} /> Compte validé — {me.payment_terms === "30j_fin_de_mois" ? "Paiement à 30j fin de mois" : "Paiement à la commande"}</p>
            {me.payment_terms === "30j_fin_de_mois" && <p className="text-xs text-green-700 mt-1">Encours autorisé : <strong>{(me.credit_limit || 0).toFixed(0)} € HT</strong> · Utilisé : {(me.outstanding || 0).toFixed(2)} €</p>}
          </div>
        )}

        <div className="space-y-3">
          {DOC_LIST.map((d) => (
            <div key={d.k} className="flex items-center justify-between rounded-xl border border-border p-3" data-testid={`doc-${d.k}`}>
              <div className="flex items-center gap-3 min-w-0">
                {docs[d.k] ? <CheckCircle2 size={18} className="text-green-600 shrink-0" /> : <Clock size={18} className="text-stone shrink-0" />}
                <div className="min-w-0"><p className="text-sm font-medium text-forest">{d.l}</p>{docs[d.k] && <p className="text-xs text-stone truncate">{docs[d.k].filename}</p>}</div>
              </div>
              {status !== "approuve" && status !== "en_attente" && (
                <label className="shrink-0 cursor-pointer rounded-full bg-brand/10 text-brand text-xs font-semibold px-4 py-2 hover:bg-brand hover:text-cream transition-colors flex items-center gap-1.5">
                  {busy === d.k ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} {docs[d.k] ? "Remplacer" : "Ajouter"}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid={`upload-${d.k}`} onChange={(e) => upload(d.k, e.target.files[0])} />
                </label>
              )}
            </div>
          ))}
        </div>

        {(status === "incomplet" || status === "refuse") && (
          <button onClick={submitRequest} disabled={!allUploaded || busy === "submit"} data-testid="submit-account-request"
            className="w-full mt-5 rounded-full bg-brand text-cream py-3 font-semibold hover:bg-forest transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {busy === "submit" ? <Loader2 className="animate-spin" size={18} /> : <Building2 size={18} />} {allUploaded ? "Envoyer ma demande d'ouverture de compte" : "Ajoutez les 3 documents pour continuer"}
          </button>
        )}
        {status === "en_attente" && <p className="mt-5 text-sm text-blue-700 flex items-center gap-2" data-testid="pro-pending"><Clock size={16} /> Votre demande est en cours d'examen par notre équipe. Vous pouvez commander par CB en attendant.</p>}
        {status === "refuse" && me.decision_note && <p className="mt-3 text-sm text-red-600">{me.decision_note}</p>}
      </div>
    </div>
  );
}
